import dayjs from 'dayjs';
import { ScatterPoint } from '../types';
import { ConversationSchema } from '../../../requests/api/apiTypes';
import { buildDaySeparatorLines } from '../utils';

const getConversationTooltip = (
  highChartObject: Highcharts.TooltipFormatterContextObject,
  conversations: ConversationSchema[],
) => {
  const scatterPoint = highChartObject.point as ScatterPoint;
  const conversationId = scatterPoint.custom?.conversationId;
  if (!conversations || !conversationId) {
    return '<b>Último comentario:</b> Cargando...<br/>';
  }
  const conversation = conversations.find((conv) => conv.conversationId === conversationId);
  if (!conversation) {
    return '<b>Último comentario:</b> No se encontró la conversación<br/>';
  }
  const lastComment = conversation.lastComment || 'Sin comentarios';
  return `
<div style="position: relative; min-width: 300px; max-width: 400px; word-wrap: break-word; overflow-wrap: break-word; white-space: normal;">
<b>Último comentario:</b> ${lastComment}<br/>
<span style="color: gray; font-size: 10px">${dayjs(conversation.updatedAt).format('MMM DD, YYYY HH:mm')}</span>
</div>`;
};

const getTemperatureHumidityTooltip = (
  highChartObject: Highcharts.TooltipFormatterContextObject,
  conversations: ConversationSchema[],
) => {
  if (highChartObject.series.name === 'Comentarios') {
    return getConversationTooltip(highChartObject, conversations);
  }
  const header = '<div style="font-size: 14px; font-weight: bold; margin-bottom: 8px;">Resumen</div>';
  const { point } = highChartObject;
  let temperaturePoint: Highcharts.TooltipFormatterContextObject | null = null;
  let humidityPoint: Highcharts.TooltipFormatterContextObject | null = null;
  if (highChartObject.points) {
    highChartObject.points.forEach((p) => {
      if (p.series.name.toLowerCase().includes('temperatura') || p.series.name.toLowerCase().includes('temperature')) {
        temperaturePoint = p as Highcharts.TooltipFormatterContextObject;
      }
      if (p.series.name.toLowerCase().includes('humedad') || p.series.name.toLowerCase().includes('humidity')) {
        humidityPoint = p as Highcharts.TooltipFormatterContextObject;
      }
    });
  }
  function isTooltipPoint(obj: any): obj is Highcharts.TooltipFormatterContextObject {
    return obj && typeof obj.y !== 'undefined' && obj.series && typeof obj.series.name === 'string' && typeof obj.color === 'string';
  }
  let pointsDisplay = '';
  if (isTooltipPoint(temperaturePoint)) {
    const t = temperaturePoint as Highcharts.TooltipFormatterContextObject;
    pointsDisplay += `<div style="margin: 6px 0 2px 0"><span style="color:${t.color}">\u25CF</span> ${t.series.name}: <b>${Number(t.y).toFixed(2)}</b></div>`;
  }
  if (isTooltipPoint(humidityPoint)) {
    const h = humidityPoint as Highcharts.TooltipFormatterContextObject;
    pointsDisplay += `<div style="margin: 6px 0 2px 0"><span style="color:${h.color}">\u25CF</span> ${h.series.name}: <b>${Number(h.y).toFixed(2)}</b></div>`;
  }
  if (!pointsDisplay) {
    pointsDisplay = '<div style="margin: 6px 0 2px 0">No data</div>';
  }
  const subHeader = `<div style="font-size: 12px; margin-bottom: 8px; margin-top: 4px;">${dayjs(point.x).utc().format('MMM DD, YYYY HH:mm')}</div>`;
  return `<div style="padding: 1px; min-width: 180px;">${header}${subHeader}${pointsDisplay}</div>`;
};

const getPhElectricityTooltip = (
  highChartObject: Highcharts.TooltipFormatterContextObject,
  conversations: ConversationSchema[],
) => {
  if (highChartObject.series.name === 'Comentarios') {
    return getConversationTooltip(highChartObject, conversations);
  }
  const header = '<div style="font-size: 14px; font-weight: bold; margin-bottom: 8px;">Resumen</div>';
  const { point } = highChartObject;
  let electricityPoint: Highcharts.TooltipFormatterContextObject | null = null;
  let phPoint: Highcharts.TooltipFormatterContextObject | null = null;
  if (highChartObject.points) {
    highChartObject.points.forEach((p) => {
      if (p.series.name.toLowerCase() === 'ph') {
        phPoint = p as Highcharts.TooltipFormatterContextObject;
      } else {
        electricityPoint = p as Highcharts.TooltipFormatterContextObject;
      }
    });
  }
  function isTooltipPoint(obj: any): obj is Highcharts.TooltipFormatterContextObject {
    return obj && typeof obj.y !== 'undefined' && obj.series && typeof obj.series.name === 'string' && typeof obj.color === 'string';
  }
  let pointsDisplay = '';
  if (isTooltipPoint(electricityPoint)) {
    const t = electricityPoint as Highcharts.TooltipFormatterContextObject;
    pointsDisplay += `<div style="margin: 6px 0 2px 0"><span style="color:${t.color}">\u25CF</span> ${t.series.name}: <b>${Number(t.y).toFixed(2)}</b></div>`;
  }
  if (isTooltipPoint(phPoint)) {
    const h = phPoint as Highcharts.TooltipFormatterContextObject;
    pointsDisplay += `<div style="margin: 6px 0 2px 0"><span style="color:${h.color}">\u25CF</span> ${h.series.name}: <b>${Number(h.y).toFixed(2)}</b></div>`;
  }
  if (!pointsDisplay) {
    pointsDisplay = '<div style="margin: 6px 0 2px 0">No data</div>';
  }
  const subHeader = `<div style="font-size: 12px; margin-bottom: 8px; margin-top: 4px;">${dayjs(point.x).utc().format('MMM DD, YYYY HH:mm')}</div>`;
  return `<div style="padding: 1px; min-width: 180px;">${header}${subHeader}${pointsDisplay}</div>`;
};

const getLinearTooltip = (
  highChartObject: Highcharts.TooltipFormatterContextObject,
  conversations: ConversationSchema[],
) => {
  if (highChartObject.series.name === 'Comentarios') {
    return getConversationTooltip(highChartObject, conversations);
  }
  const header = '<div style="font-size: 14px; font-weight: bold; margin-bottom: 8px;">Resumen</div>';
  const { point } = highChartObject;
  const subHeader = `<div style="font-size: 12px; margin-bottom: 8px;">${dayjs(point.x).utc().format('MMM DD, YYYY HH:mm')}</div>`;
  const pointDisplay = `<div style="margin: 4px 0"><span style="color:${point.color}">\u25CF</span> ${point.series.name}: <b>${Number(point.y).toFixed(2)}</b></div>`;
  return `<div style="padding: 1px">${header}${subHeader}${pointDisplay}</div>`;
};

const getStackedTooltip = (
  highChartObject: Highcharts.TooltipFormatterContextObject,
  conversations: ConversationSchema[],
) => {
  if (highChartObject.series.name === 'Comentarios') {
    return getConversationTooltip(highChartObject, conversations);
  }

  const header = '<div style="font-size: 14px; font-weight: bold; margin-bottom: 8px;">Resumen</div>';
  const subHeader = `<br/><div style="font-size: 12px; margin-bottom: 8px;">
    ${dayjs(highChartObject.point?.custom?.date).utc().format('MMM DD, YYYY HH:mm')}
  </div>`;
  const pointsDisplay = highChartObject.points?.map(
    (p: Highcharts.TooltipFormatterContextObject & {
      point: { custom?: { realValue?: number } }
    }) => {
      const value = p.point?.custom?.realValue ?? p.y;
      return `<br/><div><span style="color:${p.color}">\u25CF</span> ${p.series.name}: <b>${Number(value).toFixed(2)}</b></div>`;
    },
  ).join('');

  return `
    <div style="
      padding: 1px;
      background: rgba(255, 255, 255, 1);
      border-radius: 5px;">
      ${header}<br/>${subHeader}<br/>${pointsDisplay}
    </div>
  `;
};

const getYAxis = (yAxisTitle: string | null, hideYValues: boolean) => ({
  startOnTick: false,
  endOnTick: false,
  labels: {
    enabled: !hideYValues,
    format: '{value}',
    align: 'right',
    x: -2,
    style: {
      fontSize: '9px',
    },
    reserveSpace: true,
  },
  title: {
    text: yAxisTitle || null,
    style: { fontSize: '7px', fontWeight: '600' },
    margin: 3,
  },
  gridLineWidth: 1,
});

const getChartConfig = (
  chartData: any,
  height: string | number,
  hideXValues: boolean,
  yAxis?: Highcharts.YAxisOptions,
  minTimestamp?: number,
  maxTimestamp?: number,
  // eslint-disable-next-line no-unused-vars
  phenologicalStatesLabels?: {
    [key: string]: string;
  },
) => {
  const firstDay = dayjs(minTimestamp).utc().startOf('day').valueOf();
  const lastDay = dayjs(maxTimestamp).add(dayjs().utcOffset(), 'minutes').valueOf();
  return (
    {
      chart: {
        height,
      },
      xAxis: {
        type: 'datetime',
        labels: {
          enabled: !hideXValues,
          rotation: 0,
          autoRotation: [0, -15, -30, -45],
          style: { color: '#475569', fontSize: '11px', fontWeight: '500' },
        },
        dateTimeLabelFormats: {
          day: '%e %b',
          week: '%e %b',
          month: '%b %y',
          year: '%Y',
        },
        min: firstDay || undefined,
        max: lastDay || undefined,
        plotLines: buildDaySeparatorLines(chartData.absoluteCategories),
      },
      yAxis: yAxis || undefined,
    });
};

const calculateYAxisLimits = (series: any[]) => {
  let minY = Infinity;
  let maxY = -Infinity;

  if (series && series.length > 0) {
    series.forEach((seriesItem: any) => {
      if (seriesItem.data && seriesItem.data.length > 0) {
        seriesItem.data.forEach((point: any) => {
          // point can be null (charts emit null for missing days, e.g. rainfall gaps).
          if (point && typeof point.y === 'number' && !Number.isNaN(point.y)) {
            minY = Math.min(minY, point.y);
            maxY = Math.max(maxY, point.y);
          }
        });
      }
    });
  }

  if (!Number.isFinite(minY) || !Number.isFinite(maxY)) {
    minY = 0;
    maxY = 1;
  }
  // const range = maxY - minY;
  const yAxisMin = minY - 1 * Math.floor(minY / 1000);
  const yAxisMax = maxY + 1 * Math.floor(maxY / 1000);

  return {
    min: yAxisMin,
    max: yAxisMax,
  };
};

export {
  getChartConfig,
  getLinearTooltip,
  getStackedTooltip,
  getYAxis,
  getTemperatureHumidityTooltip,
  calculateYAxisLimits,
  getPhElectricityTooltip,
  getConversationTooltip,
};
